/**
 * Validation for the Width / Height / Depth fields (issue #1088).
 *
 * Every endpoint that takes a dimension — the single-object PATCH, the batch PATCH and the create
 * POSTs — types width / height / depth as `integer, minimum: 0`, so a fractional or non-numeric
 * entry can only ever be rejected. The fields used to be Ant Design `InputNumber`s pinned to
 * `precision={0}`, which rewrote such an entry instead of reporting it: `1,4` became `14`, `fff`
 * became `0`. #1088 asks for the opposite — the typed text stays on screen, a message says what is
 * wrong, and nothing is sent.
 *
 * That is the same rule `TimesReferenced` (REQ-423) already follows, with one deliberate difference:
 * that field strips non-digits at the source, and stripping is precisely what turns `1,4` into `14`.
 * Here the keystrokes are kept and validated instead.
 */

export const DIMENSION_INTEGER_MESSAGE = "Enter a whole number"
export const DIMENSION_REQUIRED_MESSAGE = "This field is required"

/** Only when the caller sets a floor above zero, so the default case keeps the shorter message. */
export const dimensionMinMessage = (min: number) => `Enter a whole number of ${min} or more`

const WHOLE_NUMBER = /^\d+$/

/**
 * The number a dimension field holds, or `null` when the text is not a whole number. Deliberately
 * strict: `Number("1.6")`, `parseInt("1.6")` and `Number("")` all produce something plausible from
 * text the endpoint would reject, which is how the reported values got through.
 */
export const parseDimensionText = (text: string): number | null => {
	const trimmed = text.trim()
	if (!WHOLE_NUMBER.test(trimmed)) return null
	return Number(trimmed)
}

type TResolveDimensionError = {
	text: string
	min: number
	isRequired: boolean
	/**
	 * Whether the user has typed in this field during this mount. An empty field is only *reported*
	 * as missing once it has been touched — the create panels (Add cabinet / countertop / other /
	 * custom) open with all three dimensions blank, and greeting them with three red messages would
	 * make the panel look broken before anyone has done anything.
	 */
	isTouched: boolean
}

/** The message to show under a dimension field, or `null` when it holds nothing to complain about. */
export const resolveDimensionError = ({ text, min, isRequired, isTouched }: TResolveDimensionError): string | null => {
	const trimmed = text.trim()
	if (trimmed === "") return isRequired && isTouched ? DIMENSION_REQUIRED_MESSAGE : null
	const parsed = parseDimensionText(trimmed)
	if (parsed === null) return DIMENSION_INTEGER_MESSAGE
	if (parsed < min) return min > 0 ? dimensionMinMessage(min) : DIMENSION_INTEGER_MESSAGE
	return null
}
